import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider, isFirebaseConfigured } from '../config/firebase';
import { isAdminUser, isKnownAdminEmail } from '../utils/adminAccess';

export type UserRole = 'admin' | 'user';

export interface AppUser {
  uid: string;
  username: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  role: UserRole;
}

const ENV_RESTART_MSG =
  'Firebase is not initialized because the dev server has not loaded the .env file yet. Please stop your terminal (Ctrl+C) and run "npm start" again.';

/**
 * Maps Firebase auth error codes to friendly messages
 */
export const formatAuthError = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: string }).code;
    switch (code) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please check your credentials.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Try signing in instead.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/popup-closed-by-user':
        return 'Google sign-in popup was closed before completing.';
      case 'auth/cancelled-popup-request':
        return 'Authentication was cancelled.';
      case 'auth/too-many-requests':
        return 'Too many requests. Please wait a minute before trying again.';
      case 'auth/network-request-failed':
        return 'Network connection failed. Please check your internet connection.';
      default:
        return (error as { message?: string }).message || 'Authentication failed. Please try again.';
    }
  }
  return error instanceof Error ? error.message : 'An unexpected authentication error occurred.';
};

/**
 * Format a Firebase user into our application User object with role
 */
export const formatAppUser = (firebaseUser: FirebaseUser, role?: UserRole): AppUser => {
  const username =
    firebaseUser.displayName ||
    (firebaseUser.email ? firebaseUser.email.split('@')[0] : 'Trader');

  const email = (firebaseUser.email || '').trim().toLowerCase();
  const isBootstrapAdmin = isKnownAdminEmail(email);
  const resolvedRole: UserRole = role ?? (isBootstrapAdmin ? 'admin' : 'user');

  return {
    uid: firebaseUser.uid,
    username,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    emailVerified: Boolean(firebaseUser.emailVerified),
    role: resolvedRole,
  };
};

/**
 * Synchronize user profile in Firestore database (creates or updates user doc)
 */
export const syncUserProfile = async (
  firebaseUser: FirebaseUser,
  token?: string | null
): Promise<UserRole> => {
  const email = (firebaseUser.email || '').trim().toLowerCase();
  const isBootstrapAdmin = isKnownAdminEmail(email) || isAdminUser({ email, uid: firebaseUser.uid }, token);
  let resolvedRole: UserRole = isBootstrapAdmin ? 'admin' : 'user';

  if (!db) {
    return resolvedRole;
  }

  try {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const data = userSnap.data();
      resolvedRole = data.role === 'admin' || isBootstrapAdmin ? 'admin' : 'user';

      if (isBootstrapAdmin && data.role !== 'admin') {
        resolvedRole = 'admin';
        await setDoc(userRef, { role: 'admin' }, { merge: true });
      }

      await setDoc(
        userRef,
        {
          lastLoginAt: serverTimestamp(),
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || data.displayName || null,
          photoURL: firebaseUser.photoURL || null,
          ...(isBootstrapAdmin ? { role: 'admin' } : {}),
        },
        { merge: true }
      );
    } else {
      await setDoc(userRef, {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || null,
        photoURL: firebaseUser.photoURL || null,
        role: resolvedRole,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.warn('Firestore syncUserProfile warning:', error);
  }

  return resolvedRole;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email: string, password: string): Promise<AppUser> => {
  if (!auth) {
    throw new Error(ENV_RESTART_MSG);
  }
  try {
    await setPersistence(auth, browserLocalPersistence);
    const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
    const role = await syncUserProfile(userCredential.user);
    return formatAppUser(userCredential.user, role);
  } catch (error) {
    throw new Error(formatAuthError(error));
  }
};

/**
 * Register a new user with email and password, and dispatch verification email
 */
export const signUpWithEmail = async (
  email: string,
  password: string,
  displayName?: string
): Promise<AppUser> => {
  if (!auth) {
    throw new Error(ENV_RESTART_MSG);
  }
  try {
    await setPersistence(auth, browserLocalPersistence);
    const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
    if (displayName?.trim()) {
      await updateProfile(userCredential.user, { displayName: displayName.trim() });
    }

    // Automatically send email verification link
    try {
      await sendEmailVerification(userCredential.user);
    } catch (emailErr) {
      console.warn('Initial sendEmailVerification error:', emailErr);
    }

    const role = await syncUserProfile(userCredential.user);
    return formatAppUser(userCredential.user, role);
  } catch (error) {
    throw new Error(formatAuthError(error));
  }
};

/**
 * Resend verification email to the currently logged in user
 */
export const sendVerificationEmail = async (): Promise<void> => {
  if (!auth || !auth.currentUser) {
    throw new Error('No user is currently signed in.');
  }
  try {
    await sendEmailVerification(auth.currentUser);
  } catch (error) {
    throw new Error(formatAuthError(error));
  }
};

/**
 * Reloads user profile from Firebase servers to check latest emailVerified status
 */
export const reloadUser = async (): Promise<AppUser | null> => {
  if (!auth || !auth.currentUser) {
    return null;
  }
  await auth.currentUser.reload();
  const role = await syncUserProfile(auth.currentUser);
  return formatAppUser(auth.currentUser, role);
};

/**
 * Sign in with Google Popup
 */
export const signInWithGoogle = async (): Promise<AppUser> => {
  if (!auth) {
    throw new Error(ENV_RESTART_MSG);
  }
  try {
    await setPersistence(auth, browserLocalPersistence);
    const userCredential = await signInWithPopup(auth, googleProvider);
    const role = await syncUserProfile(userCredential.user);
    return formatAppUser(userCredential.user, role);
  } catch (error) {
    throw new Error(formatAuthError(error));
  }
};

/**
 * Sign out current user
 */
export const signOutUser = async (): Promise<void> => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem('ns_last_activity');
      sessionStorage.removeItem('ns_last_activity');
    } catch {
      // ignore storage errors
    }
  }
  if (!auth) {
    return;
  }
  await signOut(auth);
};

/**
 * Send password reset email to user
 */
export const resetPassword = async (email: string): Promise<void> => {
  if (!auth) {
    throw new Error(ENV_RESTART_MSG);
  }
  try {
    await sendPasswordResetEmail(auth, email.trim());
  } catch (error) {
    throw new Error(formatAuthError(error));
  }
};

/**
 * Listen for auth state changes with real-time Firestore role synchronization
 */
export const subscribeToAuthChanges = (
  callback: (user: AppUser | null, token: string | null) => void
): (() => void) => {
  if (!auth) {
    callback(null, null);
    return () => {};
  }

  let unsubscribeSnapshot: (() => void) | null = null;

  try {
    const unsubscribeAuth = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }

        if (firebaseUser) {
          let token: string | null = null;
          try {
            token = await firebaseUser.getIdToken();
          } catch (tokenErr) {
            console.warn('Failed to get ID token:', tokenErr);
          }

          // Check admin access immediately from email and token claims
          const hasImmediateAdminAccess =
            isKnownAdminEmail(firebaseUser.email) ||
            isAdminUser({ email: firebaseUser.email, uid: firebaseUser.uid }, token);
          let appUser = formatAppUser(firebaseUser, hasImmediateAdminAccess ? 'admin' : undefined);
          callback(appUser, token);

          if (db) {
            try {
              const currentRole = await syncUserProfile(firebaseUser, token);
              appUser = formatAppUser(firebaseUser, currentRole);
              callback(appUser, token);

              // Attach real-time listener on the user's Firestore document
              const userRef = doc(db, 'users', firebaseUser.uid);
              unsubscribeSnapshot = onSnapshot(
                userRef,
                (docSnap) => {
                  if (docSnap.exists()) {
                    const data = docSnap.data();
                    const isStillAdmin =
                      data.role === 'admin' ||
                      isKnownAdminEmail(firebaseUser.email) ||
                      isAdminUser({ email: firebaseUser.email, uid: firebaseUser.uid }, token);
                    const latestRole: UserRole = isStillAdmin ? 'admin' : 'user';
                    if (appUser.role !== latestRole) {
                      appUser = formatAppUser(firebaseUser, latestRole);
                      callback(appUser, token);
                    }
                  }
                },
                (err) => {
                  console.warn('Firestore user snapshot warning:', err);
                }
              );
            } catch (syncErr) {
              console.warn('Failed to sync or attach Firestore listener:', syncErr);
            }
          }
        } else {
          callback(null, null);
        }
      },
      (error) => {
        console.error('Firebase onAuthStateChanged error:', error);
        callback(null, null);
      }
    );

    return () => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      unsubscribeAuth();
    };
  } catch (error) {
    console.error('Failed to attach auth state listener:', error);
    callback(null, null);
    return () => {};
  }
};

/**
 * Get current JWT ID token from active user
 */
export const getAuthToken = async (): Promise<string | null> => {
  if (!auth) {
    return null;
  }
  // Wait for initial auth state to be restored if not yet available
  if (!auth.currentUser && typeof auth.authStateReady === 'function') {
    try {
      await auth.authStateReady();
    } catch {
      // ignore
    }
  }
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }
  return currentUser.getIdToken();
};

export { isFirebaseConfigured };
